import React, {
  createContext,
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { AppResponse } from "../shared-types";
import { Service } from "../util/Service";
import Cookies from "js-cookie";

interface PortalEnv {
  BACKEND_ENDPOINT: string;
  SMARTPASS_LOGIN_URL: string;
  SMARTPASS_LOGIN_REALM: string;
  SMARTPASS_LOGIN_CLIENT_ID: string;
  [key: string]: string;
}

interface PortalProviderProps {
  context: {
    serverData?: any;
    env?: PortalEnv;
    accessToken?: string;
    ssr: boolean;
  };
}

export interface PortalContext {
  serverData?: any;
  env?: PortalEnv;
  accessToken?: string;
  backend?: Service;
  currentApp?: AppResponse;
  ssr: boolean;
  setAccessToken: (accessToken?: string) => void;
  setCurrentApp: (app?: AppResponse) => void;
  profile?: Record<string, unknown>;
  setProfile: (app: Record<string, unknown>) => void;
  noHeader?: boolean;
}

const CustomPortalContext = createContext<PortalContext>({
  setAccessToken: () => null,
  setCurrentApp: () => null,
  setProfile: () => null,
  ssr: false,
  env: {} as PortalEnv,
});

function getAccessTokenFromCookie(): string | undefined {
  if (typeof window === "undefined") {
    return undefined;
  }

  const encodedToken = Cookies.get("kcToken");
  if (!encodedToken) {
    return undefined;
  }

  try {
    return window.atob(encodedToken);
  } catch {
    return undefined;
  }
}

export const PortalProvider: FC<PortalProviderProps> = ({
  children,
  context,
}) => {
  const runtimeEnv = useMemo<PortalEnv>(() => {
    const browserEnv =
      typeof window !== "undefined"
        ? {
            BACKEND_ENDPOINT: window.BACKEND_ENDPOINT,
            SMARTPASS_LOGIN_URL: window.SMARTPASS_LOGIN_URL,
            SMARTPASS_LOGIN_REALM: window.SMARTPASS_LOGIN_REALM,
            SMARTPASS_LOGIN_CLIENT_ID: window.SMARTPASS_LOGIN_CLIENT_ID,
          }
        : {};

    return { ...browserEnv, ...context?.env } as PortalEnv;
  }, [context?.env]);

  const [accessToken, setAccessToken] = useState<string | undefined>(
    () => context?.accessToken || getAccessTokenFromCookie()
  );

  const serverRenderedApp = useMemo(() => {
    const appFromGlobals =
      typeof window !== "undefined" ? window?.APP : undefined;
    return context?.serverData?.currentApp || appFromGlobals;
  }, [context?.serverData?.currentApp]);

  const serverRenderedProfile = useMemo(() => {
    const profileFromGlobals =
      typeof window !== "undefined" ? window?.PROFILE : undefined;
    return context?.serverData?.profile || profileFromGlobals;
  }, [context?.serverData?.profile]);

  const backendEndpoint = useMemo(() => {
    const backendEndpointFromGlobals =
      typeof window !== "undefined" ? window?.BACKEND_ENDPOINT : undefined;
    return runtimeEnv.BACKEND_ENDPOINT || backendEndpointFromGlobals;
  }, [runtimeEnv.BACKEND_ENDPOINT]);

  const noHeader = useMemo(() => {
    const noHeaderFromGlobals: boolean =
      typeof window !== "undefined" ? window?.__XVIA_NO_HEADER : false;
    const noHeaderFromCookie: boolean = Cookies.get("xviaNoHeader") === "true";
    return (
      context?.serverData?.noHeader ||
      noHeaderFromGlobals ||
      noHeaderFromCookie ||
      false
    );
  }, [context?.serverData?.noHeader]);

  const ssr = useMemo(() => {
    const ssrFromGlobal = typeof window !== "undefined" ? window?.SSR : false;
    return context?.ssr || ssrFromGlobal;
  }, [context?.ssr]);

  const [currentApp, setCurrentApp] = useState<AppResponse | undefined>(
    serverRenderedApp
  );
  const [profile, setProfile] = useState<Record<string, unknown>>(
    serverRenderedProfile
  );

  const backend = useRef<Service>(
    new Service({
      endpoint: backendEndpoint as string,
      accessToken,
    })
  );

  const contextData: PortalContext = {
    serverData: context?.serverData,
    env: runtimeEnv,
    backend: backend.current,
    ssr,
    accessToken,
    setAccessToken,
    setCurrentApp,
    currentApp,
    profile,
    setProfile,
    noHeader,
  };

  const updateBasePath = useCallback(() => {
    if (typeof window !== "undefined") {
      window.__APP_BASE__ = `/app/${currentApp?.slug}`;
    }
  }, [currentApp?.slug]);

  useEffect(() => {
    backend.current.setAccessToken(accessToken);
    updateBasePath();
  }, [accessToken, currentApp?.slug, updateBasePath]);

  updateBasePath();

  return (
    <CustomPortalContext.Provider value={contextData}>
      {children}
    </CustomPortalContext.Provider>
  );
};

export function usePortalProvider(): PortalContext {
  return useContext(CustomPortalContext);
}
